import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import ReactSelect from 'react-select';
import type { MultiValue, SingleValue, StylesConfig } from 'react-select';
import styled from 'styled-components';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import { DarkGreen } from '../../../app/theme/tokens/colors';
import { PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs';
import { GreenLabel, YellowLabel } from '../../../shared/messages/labels';
import { Spinner } from '../../../shared/ui/Spinners';
import { SimplePopup } from '../../../shared/overlays';
import {
  delUser,
  listUsers,
} from '../../../services/configuration/userManagementService';
import { cognitoStatusMapping } from '../../../utils/auth';
import { UserForm } from './form';

type UserId = string | number;
type SortOrder = 'asc' | 'desc';
type UserSortColumn =
  | 'name'
  | 'email'
  | 'company'
  | 'profile'
  | 'email_verified'
  | 'status'
  | 'enabled';

interface CompanyRecord {
  company_name?: string;
  empresa?: string;
  name?: string;
  cnpj?: string | number;
}

interface NormalizedCompany {
  name: string;
  cnpj: string;
}

interface UserRecord {
  id?: UserId;
  name?: string;
  email?: string;
  companies?: CompanyRecord[];
  email_verified?: boolean | string | number;
  status?: string;
  enabled?: boolean | string | number;
  [key: string]: unknown;
}

interface SelectOption {
  value: string;
  label: string;
}

interface UserTableColumn {
  id: UserSortColumn;
  label: string;
  minWidth: number;
  align?: 'center';
}

interface CompanyCellProps {
  companies?: CompanyRecord[];
  fallbackName?: unknown;
  fallbackCnpj?: unknown;
}

const ToolBar = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border-top: solid 1px #b9b9b9;
  padding: 10px 0;
  flex-shrink: 0;
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
  min-width: 280px;
`;

const FilterField = styled.div`
  width: 240px;
  min-width: 180px;

  &.filter-value {
    width: 320px;
  }
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #555;
`;

const selectStyles: StylesConfig<SelectOption, boolean> = {
  control: (base, state) => ({
    ...base,
    minHeight: '36px',
    borderColor: state.isFocused ? '#4285f4' : '#d9d9d9',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(66,133,244,0.15)' : 'none',
    '&:hover': { borderColor: '#4285f4' },
    borderRadius: '4px',
    fontSize: '13px',
  }),
  placeholder: (base) => ({ ...base, color: '#aaa', fontSize: '13px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: state.isSelected
      ? '#e8f0fe'
      : state.isFocused
      ? '#f8f9fa'
      : 'white',
    color: state.isSelected ? '#1a73e8' : '#333',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#eef3ee' }),
  multiValueLabel: (base) => ({ ...base, color: '#2f5d3a', fontSize: '12px' }),
  valueContainer: (base) => ({
    ...base,
    maxHeight: '72px',
    overflowY: 'auto',
    alignContent: 'flex-start',
    paddingTop: '4px',
    paddingBottom: '4px',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ─── Badge neutro para perfil ─────────────────────────────────────────────────
const ProfileBadge = styled.span<{ $highlight?: boolean }>`
  display: inline-block;
  background: #f1f3f4;
  color: #5f6368;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 8px;
  white-space: nowrap;

  ${({ $highlight }) =>
    $highlight &&
    `
    background: #edf7ef;
    color: #2f5d3a;
    border-color: #cfe6d4;
  `}
`;

// ─── Célula de empresa com expansão por clique ────────────────────────────────
const CompanyCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CompanyFirstRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CompanyLine = styled.div`
  font-size: 13px;
  color: #333;
  line-height: 1.3;

  span {
    display: block;
    font-size: 11px;
    color: #9e9e9e;
    margin-top: 1px;
  }
`;

const ExpandIcon = styled.button`
  background: #f1f3f4;
  border: 1px solid #dadce0;
  padding: 3px 5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: #5f6368;
  border-radius: 4px;
  transition: background 0.15s, border-color 0.15s;
  flex-shrink: 0;
  margin-left: auto;

  &:hover {
    background: #e8eaed;
    border-color: #c4c7c5;
  }
`;

const ExtraList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 6px;
  border-left: 2px solid #e8eaed;
  margin-left: 2px;
`;

const ExtraItem = styled.div`
  font-size: 13px;
  color: #333;
  line-height: 1.3;

  span {
    display: block;
    font-size: 11px;
    color: #9e9e9e;
    margin-top: 1px;
  }
`;

const CompanyCell = ({
  companies,
  fallbackName,
  fallbackCnpj,
}: CompanyCellProps) => {
  const [expanded, setExpanded] = useState(false);
  const list =
    Array.isArray(companies) && companies.length > 0
      ? companies
          .map((company) => ({
            name: String(
              company.company_name ?? company.empresa ?? company.name ?? ''
            ),
            cnpj: String(company.cnpj ?? ''),
          }))
          .filter((company) => company.name || company.cnpj)
      : null;

  if (!list) {
    return (
      <CompanyLine>
        <>
          {String(fallbackName ?? '') || '—'}
          {fallbackCnpj && <span>{String(fallbackCnpj)}</span>}
        </>
      </CompanyLine>
    );
  }

  const first = list[0];
  const rest = list.slice(1);

  return (
    <CompanyCellWrapper>
      <CompanyFirstRow>
        <CompanyLine>
          {first.name}
          <span>{first.cnpj}</span>
        </CompanyLine>
        {rest.length > 0 && (
          <ExpandIcon
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? 'Recolher empresas'
                : `Ver mais ${rest.length} empresa(s)`
            }
            title={expanded ? 'Recolher' : `Ver mais ${rest.length} empresa(s)`}
          >
            {expanded ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
          </ExpandIcon>
        )}
      </CompanyFirstRow>

      {expanded && rest.length > 0 && (
        <ExtraList>
          {rest.map((c, idx) => (
            <ExtraItem key={idx}>
              {c.name}
              <span>{c.cnpj}</span>
            </ExtraItem>
          ))}
        </ExtraList>
      )}
    </CompanyCellWrapper>
  );
};

// ─── Resolve perfil dominante ─────────────────────────────────────────────────
const resolveProfile = (user: UserRecord): string | null => {
  if (user['custom:isAdmin'] === '1') return 'Admin';
  if (user['custom:isWhiteMartins'] === '1') return 'White Martins';
  if (user['custom:isManager'] === '1') return 'Gerente';
  if (user['custom:isGeneralist'] === '1') return 'Geral';
  if (user['custom:isSpecialist'] === '1') return 'Especialista';
  return null;
};

const PROFILE_OPTIONS: SelectOption[] = [
  'Admin',
  'White Martins',
  'Gerente',
  'Geral',
  'Especialista',
].map((profile) => ({
  value: profile,
  label: profile,
}));

const FILTER_FIELD_OPTIONS: SelectOption[] = [
  { value: 'name', label: 'Nome' },
  { value: 'company', label: 'Empresa' },
  { value: 'profile', label: 'Perfil' },
];

const PERMISSION_OPTIONS: SelectOption[] = [
  { value: 'custom:accessImplantacao', label: 'Implantação' },
  { value: 'custom:accessAssistencia', label: 'Assistência Técnica' },
  { value: 'custom:accessRecarga', label: 'Recarga' },
  { value: 'custom:accessRecolhimento', label: 'Recolhimento' },
  { value: 'custom:accessVenda', label: 'Descartáveis' },
  { value: 'custom:accessSubalocacao', label: 'Sublocação' },
  { value: 'custom:accessReport', label: 'Relatórios' },
  { value: 'custom:canEditPaymentList', label: 'Editar lista de pagamento' },
  { value: 'custom:canSeeAllCnpjs', label: 'Visualizar todos os CNPJs' },
];

const normalizeText = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const isEnabledFlag = (value: unknown): boolean =>
  value === true || value === '1' || value === 1;

const getUserCompanies = (user: UserRecord): NormalizedCompany[] => {
  const companies = Array.isArray(user.companies) ? user.companies : [];
  const normalized = companies
    .map((company) => ({
      name: String(
        company.company_name ?? company.empresa ?? company.name ?? ''
      ),
      cnpj: String(company.cnpj ?? ''),
    }))
    .filter((company) => company.name || company.cnpj);

  if (normalized.length > 0) return normalized;

  return [
    {
      name: String(user['custom:companyName'] ?? ''),
      cnpj: String(user['custom:cnpj'] ?? ''),
    },
  ].filter((company) => company.name || company.cnpj);
};

const getCompanyOptionValue = (company: NormalizedCompany): string =>
  `${normalizeText(company.name)}|${String(company.cnpj ?? '').replace(
    /\D/g,
    ''
  )}`;

const getCompanyOptionLabel = (company: NormalizedCompany): string => {
  if (company.name && company.cnpj) return `${company.name} — ${company.cnpj}`;
  return company.name || company.cnpj;
};

const makeUniqueOptions = (values: SelectOption[]): SelectOption[] => {
  const map = new Map();
  values.forEach((option) => {
    if (!option?.value || !option?.label) return;
    if (!map.has(option.value)) map.set(option.value, option);
  });
  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })
  );
};

const sortUsersByName = (list: UserRecord[]): UserRecord[] =>
  [...list].sort((a, b) => {
    const nameA = normalizeText(a.name);
    const nameB = normalizeText(b.name);
    if (!nameA && !nameB) return 0;
    if (!nameA) return 1;
    if (!nameB) return -1;
    return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
  });

const USER_TABLE_COLUMNS: UserTableColumn[] = [
  { id: 'name', label: 'Nome', minWidth: 180 },
  { id: 'email', label: 'Email', minWidth: 220 },
  { id: 'company', label: 'Empresa', minWidth: 250 },
  { id: 'profile', label: 'Perfil', minWidth: 140, align: 'center' },
  {
    id: 'email_verified',
    label: 'Email verificado',
    minWidth: 145,
    align: 'center',
  },
  { id: 'status', label: 'Status', minWidth: 130, align: 'center' },
  { id: 'enabled', label: 'Habilitado', minWidth: 130, align: 'center' },
];

const getUserSortValue = (user: UserRecord, column: UserSortColumn): string => {
  if (column === 'company') {
    return getUserCompanies(user).map(getCompanyOptionLabel).join(' ');
  }

  if (column === 'profile') return resolveProfile(user) ?? '';
  if (column === 'email_verified') return user.email_verified ? 'Sim' : 'Não';
  if (column === 'status') return cognitoStatusMapping(user.status) ?? '';
  if (column === 'enabled') return user.enabled ? 'Habilitado' : 'Desabilitado';

  return String(user[column] ?? '');
};

const sortUsers = (
  list: UserRecord[],
  order: SortOrder,
  orderBy: UserSortColumn
): UserRecord[] =>
  [...list].sort((firstUser, secondUser) => {
    const first = normalizeText(getUserSortValue(firstUser, orderBy));
    const second = normalizeText(getUserSortValue(secondUser, orderBy));
    if (!first && !second) return 0;
    if (!first) return 1;
    if (!second) return -1;

    const comparison = first.localeCompare(second, 'pt-BR', {
      sensitivity: 'base',
      numeric: true,
    });

    return order === 'asc' ? comparison : -comparison;
  });

const normalizeUsersResponse = (payload: unknown): UserRecord[] => {
  if (Array.isArray(payload)) return payload as UserRecord[];
  if (!payload || typeof payload !== 'object') return [];

  const response = payload as Record<string, unknown>;
  if (Array.isArray(response.data)) return response.data as UserRecord[];
  if (Array.isArray(response.body)) return response.body as UserRecord[];
  return [];
};

export const UserManagementConfig = (): ReactElement => {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [currentUser, setCurrentUser] = useState<Record<string, unknown>>({});
  const [shouldShowEditPopup, setShouldShowEditPopup] = useState(false);
  const [shouldShowDeletePopup, setShouldShowDeletePopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [emailDelete, setEmailDelete] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [selectedFilterField, setSelectedFilterField] =
    useState<SelectOption | null>(null);
  const [selectedFilterValues, setSelectedFilterValues] = useState<
    SelectOption[]
  >([]);
  const [permissionFilters, setPermissionFilters] = useState<SelectOption[]>(
    []
  );
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<UserSortColumn>('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const normalizedUsers = useMemo(
    () => (Array.isArray(users) ? users : []),
    [users]
  );

  const nameOptions = useMemo<SelectOption[]>(
    () =>
      makeUniqueOptions(
        normalizedUsers.map((user) => ({
          value: normalizeText(user.name),
          label: user.name ?? '',
        }))
      ),
    [normalizedUsers]
  );

  const companyOptions = useMemo<SelectOption[]>(
    () =>
      makeUniqueOptions(
        normalizedUsers.flatMap((user) =>
          getUserCompanies(user).map((company) => ({
            value: getCompanyOptionValue(company),
            label: getCompanyOptionLabel(company),
          }))
        )
      ),
    [normalizedUsers]
  );

  const selectedFilterOptions = useMemo(() => {
    if (selectedFilterField?.value === 'name') return nameOptions;
    if (selectedFilterField?.value === 'company') return companyOptions;
    if (selectedFilterField?.value === 'profile') return PROFILE_OPTIONS;
    return [];
  }, [selectedFilterField, nameOptions, companyOptions]);

  const selectedFilterPlaceholder = selectedFilterField
    ? `Filtrar ${selectedFilterField.label.toLowerCase()}...`
    : 'Selecione um filtro primeiro';

  const filteredUsers = useMemo(() => {
    const selectedValues = new Set(
      selectedFilterValues.map((option) => option.value)
    );
    const selectedPermissions = permissionFilters.map((option) => option.value);
    const filterType = selectedFilterField?.value;

    return sortUsersByName(
      normalizedUsers.filter((user) => {
        if (selectedValues.size > 0) {
          if (
            filterType === 'name' &&
            !selectedValues.has(normalizeText(user.name))
          ) {
            return false;
          }

          if (filterType === 'company') {
            const userCompanyValues = getUserCompanies(user).map(
              getCompanyOptionValue
            );
            if (!userCompanyValues.some((value) => selectedValues.has(value))) {
              return false;
            }
          }

          if (filterType === 'profile') {
            const profile = resolveProfile(user);
            if (!profile || !selectedValues.has(profile)) return false;
          }
        }

        if (
          filterType === 'profile' &&
          selectedPermissions.length > 0 &&
          !selectedPermissions.some((permission) =>
            isEnabledFlag(user[permission])
          )
        ) {
          return false;
        }

        return true;
      })
    );
  }, [
    normalizedUsers,
    selectedFilterField,
    selectedFilterValues,
    permissionFilters,
  ]);

  const sortedUsers = useMemo(
    () => sortUsers(filteredUsers, order, orderBy),
    [filteredUsers, order, orderBy]
  );
  const lastPage = Math.max(0, Math.ceil(sortedUsers.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const visibleUsers = sortedUsers.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const requestSort = (property: UserSortColumn): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const fetchUsers = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const u = await listUsers();
      setUsers(normalizeUsersResponse(u));
    } catch (error: unknown) {
      console.warn('Erro ao buscar usuários da API:', error);
      setUsers([]);
    } finally {
      setCurrentUser({});
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const showDeletePopup = (user: UserRecord): void => {
    setEmailDelete(user.email ?? '');
    setDeleteError('');
    setShouldShowDeletePopup(true);
  };

  const showEditPopup = (user: UserRecord): void => {
    setCurrentUser({});
    const parsed = {
      id: user.id,
      name: user.name,
      email: user.email,
      cnpj: user['custom:cnpj'],
      companyName: user['custom:companyName'],
      isSpecialist: user['custom:isSpecialist'],
      isGeneralist: user['custom:isGeneralist'],
      isManager: user['custom:isManager'],
      isWhiteMartins: user['custom:isWhiteMartins'],
      canSeeAllCnpjs: user['custom:canSeeAllCnpjs'],
      isAdmin: user['custom:isAdmin'],
      patientV2Profile: user['custom:patientV2Profile'],
      accessImplantacao: user['custom:accessImplantacao'],
      accessAssistencia: user['custom:accessAssistencia'],
      accessRecarga: user['custom:accessRecarga'],
      accessRecolhimento: user['custom:accessRecolhimento'],
      accessVenda: user['custom:accessVenda'],
      accessSubalocacao: user['custom:accessSubalocacao'],
      accessReport: user['custom:accessReport'],
      canEditPaymentList: user['custom:canEditPaymentList'],
      email_verified: user.email_verified,
      status: user.status,
      enabled: user.enabled,
      companies: user.companies ?? [],
    };
    setCurrentUser(parsed);
    setIsEditing(true);
    setShouldShowEditPopup(true);
  };

  const closeUserPopup = () => {
    setShouldShowEditPopup(false);
    setIsEditing(false);
    setCurrentUser({});
  };

  const onUserSaved = () => {
    closeUserPopup();
    fetchUsers();
  };

  const DeleteUserPopup = () => (
    <SimplePopup
      width={400}
      height={250}
      zIndex={1000}
      outlet={
        <div style={{ display: 'block' }}>
          <p style={{ textAlign: 'center', fontWeight: '500' }}>
            Tem certeza que deseja excluir este usuário? &quot;
            {emailDelete}
            &quot;
          </p>
          {deleteError && (
            <p
              style={{
                color: '#b00020',
                marginTop: 10,
                marginBottom: 10,
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              {deleteError}
            </p>
          )}
          {isDeleting && (
            <div
              style={{ width: '100%', textAlign: 'center', padding: '10px 0' }}
            >
              <Spinner />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SecondaryButton
              style={{ width: '100px' }}
              disabled={isDeleting}
              onClick={() => setShouldShowDeletePopup(false)}
            >
              Não
            </SecondaryButton>
            <PrimaryButton
              style={{ width: '100px' }}
              disabled={isDeleting}
              onClick={deleteUser}
            >
              {isDeleting ? 'Excluindo...' : 'Sim'}
            </PrimaryButton>
          </div>
        </div>
      }
    />
  );

  const EditUserPopup = () => (
    <SimplePopup
      width={950}
      height={600}
      zIndex={1000}
      outlet={
        <div
          style={{
            padding: '10px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '15px',
              borderBottom: '2px solid #e0e0e0',
              flexShrink: 0,
            }}
          >
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  fontSize: '24px',
                  color: '#333',
                  fontWeight: 600,
                  margin: 0,
                  letterSpacing: '-0.5px',
                }}
              >
                {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
              </h2>
              <p
                style={{ fontSize: '14px', color: '#666', margin: '5px 0 0 0' }}
              >
                {isEditing
                  ? 'Atualize as informações do usuário'
                  : 'Preencha os dados para criar um novo usuário'}
              </p>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <UserForm
              id={currentUser.id ?? ''}
              name={currentUser.name ?? ''}
              email={currentUser.email ?? ''}
              cnpj={currentUser.cnpj ?? ''}
              isSpecialist={currentUser.isSpecialist}
              isGeneralist={currentUser.isGeneralist}
              isManager={currentUser.isManager}
              isWhiteMartins={currentUser.isWhiteMartins}
              canSeeAllCnpjs={currentUser.canSeeAllCnpjs}
              isAdmin={currentUser.isAdmin}
              patientV2Profile={currentUser.patientV2Profile}
              accessImplantacao={currentUser.accessImplantacao}
              accessAssistencia={currentUser.accessAssistencia}
              accessRecarga={currentUser.accessRecarga}
              accessRecolhimento={currentUser.accessRecolhimento}
              accessVenda={currentUser.accessVenda}
              accessSubalocacao={currentUser.accessSubalocacao}
              accessReport={currentUser.accessReport}
              canEditPaymentList={currentUser.canEditPaymentList}
              companies={
                Array.isArray(currentUser.companies)
                  ? currentUser.companies
                  : []
              }
              isEditing={isEditing}
              onAction={onUserSaved}
              onCancel={closeUserPopup}
              onSaved={onUserSaved}
            />
          </div>
        </div>
      }
    />
  );

  const deleteUser = async () => {
    setIsDeleting(true);
    setDeleteError('');
    try {
      const resp = await delUser(emailDelete);
      if (resp?.httpStatus && resp.httpStatus >= 400) {
        const msg =
          resp?.body?.message ||
          resp?.body?.error ||
          (typeof resp?.body === 'string' ? resp.body : null);
        throw new Error(
          msg || `Erro ao excluir usuário (HTTP ${resp.httpStatus})`
        );
      }
      setShouldShowDeletePopup(false);
      setIsEditing(false);
      setEmailDelete('');
      await fetchUsers();
    } catch (error: unknown) {
      console.warn('Erro ao deletar usuário:', error);
      setDeleteError(
        error instanceof Error
          ? error.message
          : 'Não foi possível excluir o usuário'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Content>
      {shouldShowEditPopup ? EditUserPopup() : <></>}
      {shouldShowDeletePopup ? DeleteUserPopup() : <></>}
      <ToolBar>
        <FilterBar>
          <FilterField>
            <FilterLabel>Filtro</FilterLabel>
            <ReactSelect<SelectOption, false>
              options={FILTER_FIELD_OPTIONS}
              value={selectedFilterField}
              onChange={(value: SingleValue<SelectOption>) => {
                setSelectedFilterField(value);
                setSelectedFilterValues([]);
                setPermissionFilters([]);
                setPage(0);
              }}
              placeholder="Escolha o filtro..."
              styles={selectStyles}
              menuPortalTarget={document.body}
              isClearable
              noOptionsMessage={() => 'Nenhum filtro encontrado'}
            />
          </FilterField>
          <FilterField className="filter-value">
            <FilterLabel>Valor</FilterLabel>
            <ReactSelect<SelectOption, true>
              isMulti
              isDisabled={!selectedFilterField}
              options={selectedFilterOptions}
              value={selectedFilterValues}
              onChange={(value: MultiValue<SelectOption>) => {
                setSelectedFilterValues(Array.from(value));
                setPage(0);
              }}
              placeholder={selectedFilterPlaceholder}
              styles={selectStyles}
              menuPortalTarget={document.body}
              noOptionsMessage={() => 'Nenhum valor encontrado'}
            />
          </FilterField>
          {selectedFilterField?.value === 'profile' && (
            <FilterField className="filter-value">
              <FilterLabel>Permissões</FilterLabel>
              <ReactSelect<SelectOption, true>
                isMulti
                options={PERMISSION_OPTIONS}
                value={permissionFilters}
                onChange={(value: MultiValue<SelectOption>) => {
                  setPermissionFilters(Array.from(value));
                  setPage(0);
                }}
                placeholder="Filtrar permissões..."
                styles={selectStyles}
                menuPortalTarget={document.body}
                noOptionsMessage={() => 'Nenhuma permissão encontrada'}
              />
            </FilterField>
          )}
        </FilterBar>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            marginLeft: 'auto',
          }}
        >
          <PrimaryButton
            data-testid="user-management-new-user-button"
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              height: '36px',
            }}
            onClick={() => {
              setIsEditing(false);
              setCurrentUser({});
              setShouldShowEditPopup(true);
            }}
          >
            Novo usuário
          </PrimaryButton>
        </div>
      </ToolBar>

      <TableWrapper>
        {isLoading ? (
          <Box sx={{ width: '100%', textAlign: 'center', py: 2.5 }}>
            <Spinner />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                border: '1px solid #e6e6e6',
                borderRadius: 1,
              }}
            >
              <MuiTable
                stickyHeader
                size="small"
                aria-label="Gerenciamento de usuários"
              >
                <TableHead>
                  <TableRow>
                    {USER_TABLE_COLUMNS.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          minWidth: column.minWidth,
                          backgroundColor: '#20362e',
                          color: 'white',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => requestSort(column.id)}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': {
                              color: 'white !important',
                            },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 110,
                        backgroundColor: '#20362e',
                        color: 'white',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleUsers.map((user) => {
                    const profile = resolveProfile(user);
                    const isWhiteMartinsWithAllCnpjs =
                      user['custom:isWhiteMartins'] === '1' &&
                      String(user['custom:canSeeAllCnpjs'] ?? '') !== '0';

                    return (
                      <TableRow hover key={user.id || user.email}>
                        <TableCell
                          sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          {user.name || '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {user.email || '—'}
                        </TableCell>
                        <TableCell>
                          <CompanyCell
                            companies={user.companies}
                            fallbackName={user['custom:companyName']}
                            fallbackCnpj={user['custom:cnpj']}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {profile ? (
                            <ProfileBadge
                              $highlight={isWhiteMartinsWithAllCnpjs}
                            >
                              {profile}
                            </ProfileBadge>
                          ) : (
                            <span style={{ color: '#aaa', fontSize: '13px' }}>
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {user.email_verified ? 'Sim' : 'Não'}
                        </TableCell>
                        <TableCell align="center">
                          {cognitoStatusMapping(user.status) || '—'}
                        </TableCell>
                        <TableCell align="center">
                          {user.enabled ? (
                            <GreenLabel style={{ textTransform: 'capitalize' }}>
                              Habilitado
                            </GreenLabel>
                          ) : (
                            <YellowLabel
                              style={{ textTransform: 'capitalize' }}
                            >
                              Desabilitado
                            </YellowLabel>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            justifyContent="center"
                            spacing={0.25}
                          >
                            <Tooltip title="Excluir usuário">
                              <IconButton
                                size="small"
                                aria-label={`Excluir usuário ${
                                  user.email || user.name || ''
                                }`}
                                onClick={() => showDeletePopup(user)}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar usuário">
                              <IconButton
                                size="small"
                                aria-label={`Editar usuário ${
                                  user.email || user.name || ''
                                }`}
                                onClick={() => showEditPopup(user)}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {visibleUsers.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={USER_TABLE_COLUMNS.length + 1}
                        align="center"
                        sx={{ py: 5 }}
                      >
                        Nenhum usuário encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </MuiTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedUsers.length}
              page={currentPage}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Linhas por página"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count}`
              }
              sx={{ flexShrink: 0, overflow: 'hidden' }}
            />
          </>
        )}
      </TableWrapper>
    </Content>
  );
};
